import React, { useState, useEffect } from 'react';
import { useConfig } from '../context/ConfigContext';
import api from '../api/axios';

export default function CircuitMapPage() {
  const { config } = useConfig();
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const FTP_BASE = 'https://ftp.pcnpublicidad.com/boehringer/concentrese';

  const fetchCircuitStatus = async () => {
    try {
      const res = await api.get('/stations/user-status');
      setStations(res.data);
    } catch (err) {
      console.error('Error al cargar las estaciones del circuito:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCircuitStatus();
  }, []);

  // Coordenadas fijas distribuidas perfectamente en 12 estaciones clave a lo largo de todo el circuito F1
  const stationCoords = [
    { x: 390, y: 360 }, // 1 (Línea de Meta / Salida)
    { x: 210, y: 360 }, // 2 (Recta Principal de Pits)
    { x: 105, y: 315 }, // 3 (Curva 1 - Sector Izquierdo)
    { x: 110, y: 220 }, // 4 (Chicana Lateral 3-4)
    { x: 100, y: 65  }, // 5 (Curva Rápida Zona Alta)
    { x: 230, y: 30  }, // 6 (Vértice Horquilla Superior)
    { x: 300, y: 115 }, // 7 (Bajada a Gran Recta Central)
    { x: 430, y: 215 }, // 8 (Centro Gran Recta de Velocidad)
    { x: 495, y: 155 }, // 9 (Chicana Sector Derecho)
    { x: 670, y: 185 }, // 10 (Curva Peraltada Extremo Derecho)
    { x: 735, y: 260 }, // 11 (Vértice Derecho Inferior)
    { x: 640, y: 345 }, // 12 (Horquilla Final Retorno a Meta)
  ];

  // Helper para convertir cualquier texto a Capitalize / Title Case respetando acrónimos o siglas
  const toCapitalize = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => {
        if (!word) return '';
        // Preservar siglas conocidas como LEGO, F1, QR, etc.
        if (['lego', 'f1', 'qr', 'bi', 'vip'].includes(word)) return word.toUpperCase();
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  return (
    <div
      className="min-h-screen bg-[#07241c] text-white flex flex-col items-center p-4 sm:p-6 space-y-6 bg-cover bg-center"
      style={{ backgroundImage: `url(${FTP_BASE}/fondo.png)` }}
    >
      {/* Circuit Header */}
      <div className="w-full max-w-5xl bg-[#0b382c]/90 border-2 border-[#00e676]/50 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden backdrop-blur-md">
        <div className="relative z-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <span className="bg-[#00e676]/20 text-[#00e676] border border-[#00e676]/40 text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-widest">
              Fórmula 1 — Circuito Boehringer Ingelheim
            </span>
            <h1 className="text-2xl sm:text-3xl font-black mt-3 text-white tracking-tight uppercase">
              Mapa de Estaciones del Circuito
            </h1>
            <p className="text-xs sm:text-sm text-slate-200 mt-2 max-w-2xl font-medium leading-relaxed">
              Consulte el estado de su recorrido en la pista. Las estaciones marcadas en <strong className="text-red-400 font-black">ROJO</strong> corresponden a las que usted ya ha registrado y las estaciones en <strong className="text-amber-300 font-black">AMARILLO</strong> son las que se encuentran pendientes por visitar.
            </p>
          </div>

          {/* Convención de Colores */}
          <div className="flex flex-col gap-2 bg-[#07241c] p-4 rounded-2xl border border-[#00e676]/30 text-xs font-bold shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-red-600 border-2 border-red-300 shadow-md" />
              <span className="text-white">Registrada (Completada)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-amber-400 border-2 border-amber-200 shadow-md animate-pulse" />
              <span className="text-white">Pendiente por Visitar</span>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-slate-300 font-bold bg-[#0b382c]/80 rounded-3xl border border-[#00e676]/40 shadow-xl max-w-5xl w-full">
          Cargando mapa interactivo del circuito...
        </div>
      ) : (
        /* Interactive SVG Map Container */
        <div className="w-full max-w-5xl bg-[#0b382c]/90 rounded-3xl border-4 border-[#00e676]/60 shadow-2xl p-4 sm:p-6 relative backdrop-blur-md">
          <div className="relative w-full aspect-[800/500] max-w-5xl mx-auto overflow-hidden rounded-2xl bg-[#07241c]/80 border border-[#00e676]/30">

            {/* SVG Track & Station Pins */}
            <svg viewBox="0 0 800 500" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
              {/* Sombra / Borde Pista Neón */}
              <path
                d="M 390 360 C 230 360, 105 350, 105 325 C 80 290, 100 270, 130 250 C 145 235, 65 225, 55 195 C 45 165, 75 95, 95 60 C 120 30, 140 85, 170 98 C 185 105, 175 35, 230 30 C 265 20, 300 75, 315 82 C 330 90, 255 130, 265 145 C 280 155, 400 205, 430 215 C 460 225, 485 248, 505 248 C 520 248, 470 160, 485 148 C 500 135, 615 165, 655 180 C 695 195, 750 215, 750 240 C 750 265, 720 315, 690 335 C 660 355, 610 280, 590 300 C 570 320, 620 360, 580 360 C 500 360, 440 360, 390 360 Z"
                fill="none"
                stroke="#00e676"
                strokeWidth="20"
                strokeLinejoin="round"
                strokeLinecap="round"
                opacity="0.3"
              />
              {/* Trazo Central de la Pista */}
              <path
                d="M 390 360 C 230 360, 105 350, 105 325 C 80 290, 100 270, 130 250 C 145 235, 65 225, 55 195 C 45 165, 75 95, 95 60 C 120 30, 140 85, 170 98 C 185 105, 175 35, 230 30 C 265 20, 300 75, 315 82 C 330 90, 255 130, 265 145 C 280 155, 400 205, 430 215 C 460 225, 485 248, 505 248 C 520 248, 470 160, 485 148 C 500 135, 615 165, 655 180 C 695 195, 750 215, 750 240 C 750 265, 720 315, 690 335 C 660 355, 610 280, 590 300 C 570 320, 620 360, 580 360 C 500 360, 440 360, 390 360 Z"
                fill="none"
                stroke="#07241c"
                strokeWidth="10"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              <path
                d="M 390 360 C 230 360, 105 350, 105 325 C 80 290, 100 270, 130 250 C 145 235, 65 225, 55 195 C 45 165, 75 95, 95 60 C 120 30, 140 85, 170 98 C 185 105, 175 35, 230 30 C 265 20, 300 75, 315 82 C 330 90, 255 130, 265 145 C 280 155, 400 205, 430 215 C 460 225, 485 248, 505 248 C 520 248, 470 160, 485 148 C 500 135, 615 165, 655 180 C 695 195, 750 215, 750 240 C 750 265, 720 315, 690 335 C 660 355, 610 280, 590 300 C 570 320, 620 360, 580 360 C 500 360, 440 360, 390 360 Z"
                fill="none"
                stroke="#00e676"
                strokeWidth="3"
                strokeDasharray="6,4"
              />

              {/* Marcadores SVG amplias e independientes para máxima legibilidad móvil */}
              {stations.map((st, idx) => {
                const coord = stationCoords[idx] || { x: 400, y: 250 };
                const isFullyRegistered = st.scanned;
                const isPartiallyRegistered = st.partiallyScanned;

                let fillColor = '#fbbf24'; // Amarillo Pendiente
                let strokeColor = '#ffffff';

                if (isFullyRegistered) {
                  fillColor = '#dc2626'; // Rojo Registrada
                  strokeColor = '#ffffff';
                } else if (isPartiallyRegistered) {
                  fillColor = '#9333ea'; // Morado Progreso
                  strokeColor = '#ffffff';
                }

                return (
                  <g key={st.id} className="cursor-pointer group">
                    {/* Círculo base del Pin ampliado */}
                    <circle
                      cx={coord.x}
                      cy={coord.y}
                      r="22"
                      fill={fillColor}
                      stroke={strokeColor}
                      strokeWidth="4"
                      className="transition-transform duration-300 transform group-hover:scale-125 drop-shadow-lg"
                    />
                    {/* Número de Estación ampliado y centrado para móvil */}
                    <text
                      x={coord.x}
                      y={coord.y + 6}
                      textAnchor="middle"
                      fill={isFullyRegistered || isPartiallyRegistered ? '#ffffff' : '#07241c'}
                      fontSize="18"
                      fontWeight="900"
                      className="font-mono select-none drop-shadow-sm"
                    >
                      {idx + 1}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Tarjetas de Información de las Estaciones con Identidad Corporativa */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {stations.map((st, idx) => {
              const isFullyRegistered = st.scanned;
              const isPartiallyRegistered = st.partiallyScanned;

              return (
                <div
                  key={st.id}
                  className={`p-4 rounded-2xl border-2 transition-all flex flex-col justify-between ${isFullyRegistered
                      ? 'bg-red-950/40 border-red-500/60 text-white'
                      : isPartiallyRegistered
                        ? 'bg-purple-950/40 border-purple-500/60 text-white'
                        : 'bg-[#07241c]/90 border-[#00e676]/40 text-white'
                    }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-7 h-7 rounded-full flex items-center justify-center font-black text-xs shrink-0 ${isFullyRegistered
                            ? 'bg-red-600 text-white'
                            : isPartiallyRegistered
                              ? 'bg-purple-600 text-white'
                              : 'bg-amber-400 text-[#07241c]'
                          }`}
                      >
                        {idx + 1}
                      </span>
                      <h3 className="font-bold text-sm text-white line-clamp-1">{st.name}</h3>
                    </div>
                  </div>

                  {st.description && (
                    <p className="text-xs text-slate-300 mt-2 line-clamp-2 leading-relaxed font-medium">
                      {st.description}
                    </p>
                  )}

                  <div className="mt-4 pt-3 border-t border-white/10 flex justify-between items-center text-xs">
                    <span className="text-slate-400 font-medium">Estado:</span>
                    <span
                      className={`font-black px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider ${
                        isFullyRegistered
                          ? 'bg-red-500/20 text-red-300 border border-red-500/40'
                          : isPartiallyRegistered
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                          : 'bg-amber-400/20 text-amber-300 border border-amber-400/40'
                      }`}
                    >
                      {isFullyRegistered
                        ? 'Completada'
                        : isPartiallyRegistered
                        ? `Etapas: ${st.completedStagesCount}/${st.totalStagesCount}`
                        : 'Pendiente'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
